import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultShortcuts } from '../../../../components/DatePicker/RangeShortcuts';
import { InvestorCashFlowType } from '../../../../schemas/InvestorCashFlows.schema';
import { getPlainDateString } from '../../../../services/queries/MaggieMetricsQueries';
import { plainDateToZonedDateTime } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import { DateRangeSelector } from '../../../Finance/form-components/DateRangeSelector';
import { selectedDateStringFPState } from '../../state/FPState';
import { investorCashFlowsDateRangeJ, investorCashFlowsIncludeFeesJ, investorCashFlowsTypeJ } from './state';

const toolbarCss = css`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 0.75rem;
`;

interface IInvestorCashFlowsToolbarProps {
  /** Offers the SLP toggle only for a fund with SLP rows. */
  hasSlp: boolean;
}

export function InvestorCashFlowsToolbar({ hasSlp }: IInvestorCashFlowsToolbarProps) {
  const [includeFees, setIncludeFees] = useAtom(investorCashFlowsIncludeFeesJ);

  return (
    <div css={toolbarCss}>
      <InvestorTypeFilter hasSlp={hasSlp} />
      <InvestorCashFlowsDateFilter />
      <FormControlLabel
        control={
          <Checkbox
            size='small'
            checked={includeFees}
            onChange={(event) => setIncludeFees(event.target.checked)}
          />
        }
        label='Include fees & expenses'
      />
    </div>
  );
}

function InvestorTypeFilter({ hasSlp }: IInvestorCashFlowsToolbarProps) {
  const [type, setType] = useAtom(investorCashFlowsTypeJ);

  // A selection whose button has just disappeared falls back to Total rather than filtering invisibly.
  useEffect(() => {
    if (type === 'SLP' && !hasSlp) {
      setType('Total');
    }
  }, [type, hasSlp, setType]);

  const onChange = useCallback(
    (_: unknown, next: InvestorCashFlowType | null) => {
      // An exclusive group reports null when the active button is clicked again; keep the selection.
      if (next !== null) {
        setType(next);
      }
    },
    [setType]
  );

  return (
    <ToggleButtonGroup
      aria-label='Select investor type'
      color='secondary'
      exclusive
      size='small'
      value={type}
      onChange={onChange}
    >
      <ToggleButton value='Total'>Total</ToggleButton>
      <ToggleButton value='GP'>GP</ToggleButton>
      <ToggleButton value='LP'>LP</ToggleButton>
      {hasSlp && <ToggleButton value='SLP'>SLP</ToggleButton>}
    </ToggleButtonGroup>
  );
}

function InvestorCashFlowsDateFilter() {
  const [range, setRange] = useAtom(investorCashFlowsDateRangeJ);
  const asOf = useAtomValue(selectedDateStringFPState);
  const [start, setStart] = useState<Date | null>(() =>
    range.from ? plainDateToZonedDateTime(range.from) : null
  );
  const [end, setEnd] = useState<Date | null>(() => (range.to ? plainDateToZonedDateTime(range.to) : null));
  const asOfDate = useMemo(() => plainDateToZonedDateTime(asOf), [asOf]);
  // The rest of the tab stops at the as-of, so a window running past it is trimmed to it and one
  // starting after it is not offered at all. Compared by day: some shortcut bounds carry the time.
  const shortcuts = useMemo(
    () =>
      defaultShortcuts()
        .filter((shortcut) => getPlainDateString(shortcut.start) <= asOf)
        .map((shortcut) =>
          getPlainDateString(shortcut.end) <= asOf ? shortcut : { ...shortcut, end: asOfDate }
        ),
    [asOf, asOfDate]
  );

  useEffect(() => {
    // Reopening the picker blanks its own dates, so only a complete pick is applied: saving an empty
    // one would clear the range and pull the whole ledger behind the user's back.
    if (!start || !end) {
      return;
    }
    const next = { from: getPlainDateString(start), to: getPlainDateString(end) };
    setRange((prev) => (prev.from === next.from && prev.to === next.to ? prev : next));
  }, [start, end, setRange]);

  // Labelled from the applied range, not the selector's own picks: those reset every time the popover
  // opens, which would blank the label while a range is still in force.
  const textContent = useCallback(
    () =>
      range.from && range.to
        ? `${formatPlainDate(range.from)} - ${formatPlainDate(range.to)}`
        : `All dates to ${formatPlainDate(asOf)}`,
    [range, asOf]
  );

  return (
    <DateRangeSelector
      setSelectedStartingDate={setStart}
      setSelectedEndingDate={setEnd}
      textContent={textContent}
      initialStartDate={start ?? undefined}
      initialEndDate={end ?? undefined}
      maxDate={asOfDate}
      shortcuts={shortcuts}
    />
  );
}

// Formatted from the plain date so the label never shifts a day across the UTC boundary.
function formatPlainDate(plainDate: string) {
  return FMT.format('dateNumeric', plainDate);
}
